import { cloneDeep } from 'lodash';

import { DBDocSchema } from '../../../generic/db-doc/db-doc-schema';
import { GenericDisplayable } from '../../../generic/displayable/generic-displayable';
import { isNilOrDefaultDocId } from '../../../generic/serialization/is-nil-or-default-doc-id';
import { CommunicationWorkerSchema } from '../../communication-worker/communication-worker/generic/communication-worker/communication-worker-schema';
import { CommunicationWorkerAttributesTaskQueueSchema } from '../../communication-worker/communication-worker-attributes/communication-worker-attributes-task-queue/communication-worker-attributes-task-queue-schema';
import { ReservationOfferConfig } from '../../reservation-offer/reservation-offer-config/reservation-offer-config';
import { CommunicationTaskQueuePermissions } from '../communication-task-queue-permissions/communication-task-queue-permissions';
import { CommunicationTaskQueueWorkerListUser } from '../communication-task-queue-worker-list/communication-task-queue-worker-lists/communication-task-queue-worker-list-user/communication-task-queue-worker-list-user';
import { CommunicationTaskQueueWorkerList } from '../communication-task-queue-worker-list/generic/communication-task-queue-worker-list/communication-task-queue-worker-list';
import { CommunicationTaskQueueWorkerListName } from '../communication-task-queue-worker-list/generic/communication-task-queue-worker-list-name';

import { CommunicationTaskQueueConstructor } from './communication-task-queue-constructor';
import { CommunicationTaskQueueSchema } from './communication-task-queue-schema';

export class CommunicationTaskQueue extends GenericDisplayable {
  /////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////
  protected maxReservedWorkers!: number;

  protected reservationOfferConfig!: ReservationOfferConfig;

  protected sid!: string | undefined;

  protected workers!: CommunicationTaskQueueWorkerList[];

  protected permissions?: CommunicationTaskQueuePermissions;

  /////////////////////////////////////////////////////////////////////////////
  // Constructor
  /////////////////////////////////////////////////////////////////////////////

  constructor(parameters: CommunicationTaskQueueConstructor) {
    super(parameters);
  }

  /////////////////////////////////////////////////////////////////////////////
  // Deserialize
  /////////////////////////////////////////////////////////////////////////////

  /**
   * This static function is private, and meant to be called only by
   * SerializableObject, and subclasses
   *
   * @param validationResult
   */
  protected static _deserialize(validationResult: import('joi').ValidationResult): CommunicationTaskQueue {
    return new CommunicationTaskQueue(super._deserialize(validationResult));
  }

  /////////////////////////////////////////////////////////////////////////////
  // Serialize
  /////////////////////////////////////////////////////////////////////////////

  public serialize() {
    return super.serialize(CommunicationTaskQueue.getSchema());
  }

  /////////////////////////////////////////////////////////////////////////////
  // Schema
  /////////////////////////////////////////////////////////////////////////////

  public static getSchema(): DBDocSchema {
    return new CommunicationTaskQueueSchema();
  }

  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////
  public getMaxReservedWorkers(): number {
    return cloneDeep(this.maxReservedWorkers);
  }

  public getReservationOfferConfig(): ReservationOfferConfig {
    return cloneDeep(this.reservationOfferConfig);
  }

  public getSid(): string | undefined {
    return cloneDeep(this.sid);
  }

  public getWorkers(): CommunicationTaskQueueWorkerList[] {
    return cloneDeep(this.workers);
  }

  public getNumWorkerSlots(): number {
    const numSlots = (this.getWorkers() ?? []).map((z) => z.getNumWorkerSlots()).reduce((x, y) => x + y, 0);
    return numSlots;
  }

  public getPermissions() {
    return cloneDeep(this.permissions);
  }

  public canUserJoinQueue() {
    return this.getPermissions()?.getJoin() ?? false;
  }

  public isUserInQueue(userId: string): boolean {
    const userListWorkers = this.getWorkers().filter(
      (w) => w.getType() === CommunicationTaskQueueWorkerListName.userList,
    ) as CommunicationTaskQueueWorkerListUser[];

    return userListWorkers.some((worker) => worker.getUserIds().includes(userId));
  }

  /////////////////////////////////////////////////////////////////////////////
  // Target Worker Expression
  /////////////////////////////////////////////////////////////////////////////

  public getTwilioTargetWorkerExpression(): string | undefined {
    if (isNilOrDefaultDocId(this.getId())) {
      throw new Error(
        'getTwilioTargetWorkerExpression: User Error: This object has no id. Cannot generate a target worker expression',
      );
    }

    // Escaping is required if the id starts with a number.
    return `${CommunicationWorkerSchema.taskQueueAttributes}.\\${this.getId()}.${CommunicationWorkerAttributesTaskQueueSchema.index} >= 0`;
  }

  /**
   * Adds a user to this queue
   */
  public addUserToQueue(userId: string): void {
    if (!this.canUserJoinQueue() || this.isUserInQueue(userId)) {
      return;
    }

    const workers = this.getWorkers();
    const userListWorker = workers.find(
      (w) => w.getType() === CommunicationTaskQueueWorkerListName.userList,
    ) as CommunicationTaskQueueWorkerListUser;

    const userIds = userListWorker?.getUserIds() ?? [];

    if (!userIds.includes(userId)) {
      const updatedUserListWorker = new CommunicationTaskQueueWorkerListUser({
        type: CommunicationTaskQueueWorkerListName.userList,
        userIds: [userId, ...userIds],
      });

      const updatedWorkers = userListWorker
        ? workers.map((w) => (w === userListWorker ? updatedUserListWorker : w))
        : [...workers, updatedUserListWorker];

      this.workers = updatedWorkers;
    }
  }

  /**
   * Removes a user from this queue
   */
  public removeUserFromQueue(userId: string): void {
    const workers = this.getWorkers();

    const updatedWorkers = workers.flatMap((worker) => {
      if (worker.getType() !== CommunicationTaskQueueWorkerListName.userList) {
        return [worker];
      }

      const userListWorker = worker as CommunicationTaskQueueWorkerListUser;
      const userIds = userListWorker.getUserIds();
      if (!userIds.includes(userId)) {
        return [worker];
      }

      const updatedUserIds = userIds.filter((id) => id !== userId);
      if (updatedUserIds.length === 0) {
        return [];
      }

      return [
        new CommunicationTaskQueueWorkerListUser({
          type: CommunicationTaskQueueWorkerListName.userList,
          userIds: updatedUserIds,
        }),
      ];
    });

    this.workers = updatedWorkers;
  }
}
